import { EmploymentTypeEnum } from '../models/mortgage.model';

/**
 * Counts the calendar months completed between two dates. A month counts only
 * once its day-of-month has been reached, so partial months are never counted.
 * @param {Date} start The start of the period.
 * @param {Date} end The end of the period.
 * @returns {number} The number of completed months, negative when
 * {@link end} precedes {@link start}, or NaN when either date is invalid.
 * @example
 * completedMonthsBetween(new Date(2024, 3, 24), new Date(2024, 6, 12)); // 2
 * completedMonthsBetween(new Date(2024, 6, 28), new Date(2024, 10, 24)); // 3
 */
export function completedMonthsBetween(start: Date, end: Date): number {
  if (isNaN(start?.getTime()) || isNaN(end?.getTime())) {
    return NaN;
  }

  const months = (end.getFullYear() - start.getFullYear()) * 12
    + (end.getMonth() - start.getMonth());

  return end.getDate() < start.getDate() ? months - 1 : months;
}

/**
 * The parts of an employment that decide the period it covers.
 */
export interface EmploymentPeriod {
  employmentType?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
// The date part only: any time or offset after it is ignored, so the calendar
// date never shifts with the browser's time zone.
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T]|$)/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T]|$)/;

/**
 * Parses an employment date to local midnight. Accepts a Date, a `MM/dd/yyyy`
 * string (the mortgage model format) or a `yyyy-MM-dd` string, read as a
 * calendar date in local time.
 * @returns {Date | null} The date at local midnight, or null when missing,
 * invalid or in any other format.
 */
export function parseEmploymentDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : startOfDay(value);
  }

  const text = String(value).trim();
  const us = US_DATE.exec(text);
  if (us) {
    return calendarDate(+us[3], +us[1], +us[2]);
  }
  const iso = ISO_DATE.exec(text);
  if (iso) {
    return calendarDate(+iso[1], +iso[2], +iso[3]);
  }
  return null;
}

/**
 * Builds a local date, rejecting roll-overs such as 02/30/2024.
 */
function calendarDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * Whether a former employment's end date is after today. Such an end date is
 * invalid: the history counts it only up to today.
 */
export function isEmploymentEndDateInFuture(
  endDate: string | Date | null | undefined,
  today: Date = new Date(),
): boolean {
  const end = parseEmploymentDate(endDate);
  return end != null && end > startOfDay(today);
}

/**
 * Counts a borrower's employment history in months: the days covered by at
 * least one employment, so overlapping jobs count once and gaps between jobs do
 * not count.
 *
 * A current employment runs to today. A former employment runs to its end date,
 * capped at today, and counts nothing without a valid end date. Jobs that
 * follow on the next day are continuous.
 *
 * The covered days are turned into completed months as if they ran
 * continuously up to the last covered day: today while a job is current, or
 * the latest end date once every job has ended, so a finished history does not
 * change as time passes. A single job gives the same months as
 * {@link completedMonthsBetween}.
 * @example
 * // 04/24/2024 - 07/12/2024 and 07/28/2024 - 11/24/2024: 79 + 119 days.
 * employmentHistoryInMonths(employments, new Date(2024, 10, 24)); // 6
 */
export function employmentHistoryInMonths(
  employments: readonly EmploymentPeriod[] | null | undefined,
  today: Date = new Date(),
): number {
  const todayStart = startOfDay(today);

  const periods = (employments ?? [])
    .map(employment => toPeriod(employment, todayStart))
    .filter(period => period != null)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const stretches: { start: Date; end: Date }[] = [];
  for (const period of periods) {
    const last = stretches[stretches.length - 1];
    if (last && period.start <= addDays(last.end, 1)) {
      if (period.end > last.end) {
        last.end = period.end;
      }
    } else {
      stretches.push({ ...period });
    }
  }

  // Stretches are in date order, so the last one covering any days ends on the
  // last covered day. A lone same-day job covers none and doesn't move it.
  let coveredDays = 0;
  let lastCoveredDay: Date | null = null;
  for (const stretch of stretches) {
    const days = daysBetween(stretch.start, stretch.end);
    if (days > 0) {
      coveredDays += days;
      lastCoveredDay = stretch.end;
    }
  }

  if (!lastCoveredDay) {
    return 0;
  }
  return completedMonthsBetween(addDays(lastCoveredDay, -coveredDays), lastCoveredDay);
}

function toPeriod(employment: EmploymentPeriod, today: Date): { start: Date; end: Date } | null {
  const start = parseEmploymentDate(employment?.startDate);
  if (!start) {
    return null;
  }

  let end = today;
  if (employment.employmentType === EmploymentTypeEnum.FormerEmployer) {
    const endDate = parseEmploymentDate(employment.endDate);
    if (!endDate) {
      return null;
    }
    end = endDate < today ? endDate : today;
  }

  // A same-day job covers no days on its own but still links the jobs either side of it.
  return start <= end ? { start, end } : null;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function daysBetween(start: Date, end: Date): number {
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utc(end) - utc(start)) / MS_PER_DAY);
}
